import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// The "go Live" approval state of one task (supabase/schema_task_live_approval.sql).
// Pending: everyone sees "awaiting approval"; the people who may decide it also
// get Approve / Reject here. Rejected: the reason stays visible until the task
// is requested again or changes status. Renders nothing otherwise.
export default function LiveApprovalBanner({ taskId, taskStatus, onChanged }) {
  const [req, setReq] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/tasks/${taskId}/live-approval`);
      setReq(r.data);
    } catch {
      setReq(null);
    }
  };
  // taskStatus in the deps: approving flips the task to Live, and cancelling
  // happens when it moves elsewhere, so re-read when the status changes.
  useEffect(() => { load(); }, [taskId, taskStatus]);

  const decide = async (approve) => {
    if (!approve && !note.trim()) { toast.error("Say why you are rejecting it."); return; }
    setBusy(true);
    try {
      await api.post(`/live-approvals/${req.id}/decide`, { approve, note: note.trim() });
      toast.success(approve ? "Approved — the task is now Live" : "Rejected — the requester has been told");
      setRejecting(false);
      setNote("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  if (!req) return null;

  if (req.status === "pending") {
    return (
      <div className="mt-4 border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="live-approval-banner">
        <div className="text-sm font-semibold text-amber-900">Awaiting approval to go Live</div>
        <div className="text-xs text-amber-800 mt-0.5">
          Requested by {req.requested_by_name || "someone"}
          {req.requested_at ? ` on ${new Date(req.requested_at).toLocaleDateString()}` : ""}. It stays in{" "}
          {req.from_status || "its current status"} until the project's owner or manager approves.
        </div>
        {req.can_decide && !rejecting && (
          <div className="flex gap-2 mt-3">
            <Button size="sm" disabled={busy} onClick={() => decide(true)} data-testid="live-approve-button"
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700">Approve</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejecting(true)} data-testid="live-reject-button"
              className="rounded-sm">Reject…</Button>
          </div>
        )}
        {req.can_decide && rejecting && (
          <div className="mt-3 space-y-2">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Why is this not ready to go Live?" className="rounded-sm bg-white" data-testid="live-reject-reason" />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => decide(false)} data-testid="live-reject-confirm"
                className="rounded-sm bg-red-600 hover:bg-red-700">Reject</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => { setRejecting(false); setNote(""); }}
                className="rounded-sm">Cancel</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (req.status === "rejected" && taskStatus !== "live") {
    return (
      <div className="mt-4 border border-red-200 bg-red-50 rounded-sm p-3 text-xs text-red-800" data-testid="live-approval-rejected">
        <span className="font-semibold">Go-Live request rejected</span>
        {req.decided_by_name ? ` by ${req.decided_by_name}` : ""}
        {req.decision_note ? ` — ${req.decision_note}` : ""}
      </div>
    );
  }

  return null;
}
